import { useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { KeyRound, Trash2, ShieldCheck, AtSign } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

export default function AdminProfile() {
  const { user, setUser } = useAuth();
  const [current, setCurrent] = useState("");
  const [next1, setNext1] = useState("");
  const [next2, setNext2] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmPurge, setConfirmPurge] = useState(false);
  const [purging, setPurging] = useState(false);
  const [newLoginId, setNewLoginId] = useState("");
  const [loginIdPw, setLoginIdPw] = useState("");
  const [savingLoginId, setSavingLoginId] = useState(false);

  const changePw = async () => {
    if (next1.length < 6) {
      toast.error("新しいパスワードは6文字以上にしてください");
      return;
    }
    if (next1 !== next2) {
      toast.error("新しいパスワードの確認が一致しません");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next1 });
      toast.success("パスワードを変更しました");
      setCurrent(""); setNext1(""); setNext2("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const changeLoginId = async () => {
    if (!newLoginId || newLoginId.length < 3) {
      toast.error("ログインIDは3文字以上で入力してください");
      return;
    }
    if (!loginIdPw) {
      toast.error("確認のため現在のパスワードを入力してください");
      return;
    }
    setSavingLoginId(true);
    try {
      const { data } = await api.post("/auth/change-login-id", { new_login_id: newLoginId, password: loginIdPw });
      // 新しいトークンに差し替え
      localStorage.setItem("token", data.access_token);
      setUser({ ...user, login_id: data.login_id });
      toast.success(`ログインIDを ${data.login_id} に変更しました`);
      setNewLoginId(""); setLoginIdPw("");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingLoginId(false);
    }
  };

  const purge = async () => {
    setPurging(true);
    try {
      const { data } = await api.post("/leads/_purge_unissued");
      toast.success(`未発行のリードを ${data.deleted}件 削除しました`);
      setConfirmPurge(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPurging(false);
    }
  };

  return (
    <div className="space-y-6 max-w-2xl" data-testid="admin-profile-page">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">アカウント設定</h1>
        <p className="text-sm text-slate-500 mt-1">パスワード変更とメンテナンス操作。</p>
      </div>

      <Card className="p-5 bg-white border border-slate-200">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="w-5 h-5 text-blue-600" />
          <h2 className="font-bold text-slate-900">基本情報</h2>
        </div>
        <div className="text-sm space-y-1 text-slate-700">
          <div><span className="text-slate-500 text-xs w-24 inline-block">ログインID</span> <code className="font-mono">{user?.login_id}</code></div>
          <div><span className="text-slate-500 text-xs w-24 inline-block">表示名</span> {user?.name}</div>
          <div><span className="text-slate-500 text-xs w-24 inline-block">役割</span> 管理者</div>
        </div>
      </Card>

      <Card className="p-5 bg-white border border-slate-200">
        <div className="flex items-center gap-2 mb-3">
          <AtSign className="w-5 h-5 text-blue-600" />
          <h2 className="font-bold text-slate-900">ログインID変更</h2>
        </div>
        <p className="text-xs text-slate-500 mb-3 leading-relaxed">
          ログインIDをメールアドレス等に変更できます。変更後は新しいIDでログインしてください（ログイン中はそのまま継続）。
        </p>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-bold text-slate-700">新しいログインID (3文字以上)</Label>
            <Input type="text" value={newLoginId} onChange={(e) => setNewLoginId(e.target.value)} className="mt-1 font-mono" data-testid="new-login-id" placeholder="例: hobana@fresh-agent.com" autoComplete="off" />
          </div>
          <div>
            <Label className="text-xs font-bold text-slate-700">確認のため現在のパスワード</Label>
            <Input type="password" value={loginIdPw} onChange={(e) => setLoginIdPw(e.target.value)} className="mt-1" data-testid="login-id-pw" autoComplete="current-password" />
          </div>
          <Button onClick={changeLoginId} disabled={savingLoginId || !newLoginId || !loginIdPw} className="bg-blue-600 hover:bg-blue-700" data-testid="change-login-id-submit">
            {savingLoginId ? "変更中…" : "ログインIDを変更"}
          </Button>
        </div>
      </Card>

      <Card className="p-5 bg-white border border-slate-200">
        <div className="flex items-center gap-2 mb-3">
          <KeyRound className="w-5 h-5 text-blue-600" />
          <h2 className="font-bold text-slate-900">パスワード変更</h2>
        </div>
        <div className="space-y-3">
          <div>
            <Label className="text-xs font-bold text-slate-700">現在のパスワード</Label>
            <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} className="mt-1" data-testid="current-pw" autoComplete="current-password" />
          </div>
          <div>
            <Label className="text-xs font-bold text-slate-700">新しいパスワード (6文字以上)</Label>
            <Input type="password" value={next1} onChange={(e) => setNext1(e.target.value)} className="mt-1" data-testid="new-pw-1" autoComplete="new-password" />
          </div>
          <div>
            <Label className="text-xs font-bold text-slate-700">新しいパスワード（確認）</Label>
            <Input type="password" value={next2} onChange={(e) => setNext2(e.target.value)} className="mt-1" data-testid="new-pw-2" autoComplete="new-password" />
          </div>
          <Button onClick={changePw} disabled={saving || !current || !next1 || !next2} className="bg-blue-600 hover:bg-blue-700" data-testid="change-pw-submit">
            {saving ? "変更中…" : "パスワードを変更"}
          </Button>
        </div>
      </Card>

      <Card className="p-5 bg-rose-50/40 border border-rose-200">
        <div className="flex items-center gap-2 mb-3">
          <Trash2 className="w-5 h-5 text-rose-600" />
          <h2 className="font-bold text-rose-900">メンテナンス</h2>
        </div>
        <p className="text-xs text-rose-800 mb-3 leading-relaxed">
          「ログインID未発行」のリードを全件削除します。<br />
          ※ 既に発行済み（トライアル中）のリードは安全のため削除されません。
        </p>
        <Button variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-100" onClick={() => setConfirmPurge(true)} data-testid="purge-button">
          <Trash2 className="w-4 h-4 mr-1" />未発行リードを全削除
        </Button>
      </Card>

      <AlertDialog open={confirmPurge} onOpenChange={setConfirmPurge}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>未発行リードを全削除しますか？</AlertDialogTitle>
            <AlertDialogDescription>
              この操作は取り消せません。ログインID未発行のリード（テストデータなど）が一括で削除されます。<br />
              発行済み（トライアル中）のリードは削除されません。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={purging}>キャンセル</AlertDialogCancel>
            <AlertDialogAction onClick={purge} disabled={purging} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-purge">
              {purging ? "削除中…" : "削除する"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
